// ============================================================================
// USE PERSONAS FOR CANVAS HOOK
// Fetches all market:Persona entities for the current project.
// Used by canvas section renderers to populate linked-entity suggestions.
// ============================================================================

import { useQuery } from '@tanstack/react-query'
import { useSessionStore } from '@/stores/sessionStore'
import { entityTreeApi, personaWorkshopApi, type EntityTreeInstance } from '@/lib/api/client'
import type { PersonaWorkshopSession, PersonaData, PainPoint } from '@ispot/shared'

// ============================================================================
// QUERY KEY
// ============================================================================

export const personasForCanvasKeys = {
  all: (accountId: string, projectId: string) =>
    ['personas-for-canvas', accountId, projectId] as const,
  sessions: (accountId: string, projectId: string) =>
    ['persona-workshop-sessions-map', accountId, projectId] as const,
}

// ============================================================================
// PERSONA SESSION DATA (for mini-popover enrichment)
// ============================================================================

export interface PersonaPopoverData {
  role: string
  goals: string[]
  pains: string[]
}

/**
 * Returns a Map from personaEntityUri → workshop session data (role, goals, pains).
 * Used by canvas section persona chips to populate their mini-popover.
 */
export function usePersonaWorkshopSessionsMap(): Map<string, PersonaPopoverData> {
  const { accountId, projectId } = useSessionStore()

  const { data } = useQuery({
    queryKey: personasForCanvasKeys.sessions(accountId ?? '', projectId ?? ''),
    queryFn: async () => {
      const sessions: PersonaWorkshopSession[] = await personaWorkshopApi.listSessions(
        accountId!, projectId!
      )
      const map = new Map<string, PersonaPopoverData>()
      for (const s of sessions) {
        if (!s.personaEntityUri) continue
        const pd = s.personaData as PersonaData
        map.set(s.personaEntityUri, {
          role: pd.role || '',
          goals: (pd.goals ?? []).slice(0, 2),
          pains: (pd.pains ?? []).slice(0, 2).map(
            (p: PainPoint | string) => (typeof p === 'string' ? p : p.text)
          ),
        })
      }
      return map
    },
    enabled: Boolean(accountId && projectId),
    staleTime: 2 * 60 * 1000,
  })

  return data ?? new Map()
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Returns all `market:Persona` entities in the current project's graph.
 *
 * Results are cached for 2 minutes (staleTime) so canvas sections
 * don't re-fetch on every render. Enabled only when both accountId
 * and projectId are available.
 */
export function usePersonasForCanvas(): {
  personas: EntityTreeInstance[]
  isLoading: boolean
  isError: boolean
} {
  const { accountId, projectId } = useSessionStore()

  const { data, isLoading, isError } = useQuery({
    queryKey: personasForCanvasKeys.all(accountId ?? '', projectId ?? ''),
    queryFn: () =>
      entityTreeApi.getByClass(accountId!, projectId!, 'market:Persona'),
    enabled: Boolean(accountId && projectId),
    staleTime: 2 * 60 * 1000,
  })

  return {
    personas: data ?? [],
    isLoading,
    isError,
  }
}
